import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { hashKey, useIsMutating, useQuery } from '@tanstack/react-query'
import { core } from '@musinsa/pbo-core'
import { notice } from '@/components/pbo/notice'
import { PboConfirm } from '@/components/pbo/pboDialog'
import { PboScope } from '@/components/pbo/PboScope'
import { usePageHeaderSticky } from '@/components/pbo/pageHeaderSticky'
import request from '@/utils/request'
import { SHIPPER_TYPES } from '@/services/shipper/apis'
import { excludeAllOption, toSelectOptions, useGetCombos } from '@/services/combo/useComboQueries'
import { appendedRows } from '@/hooks/useFetchAllRows'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { usePermissionSlot } from '@/hooks/useMenuPermission'
import { GeneralInboundFilter } from './components/GeneralInboundFilter'
import { GeneralInboundTable, InboundRow } from './components/GeneralInboundTable'
import { GR07_GRID_CD, GR07_TABLE_ID } from './components/consts'
import { generalInboundQueryKeys, GeneralInboundParams, GeneralInboundRow, useGetInboundList } from './queries/useGetInboundList'
import { GR01_DELETE_MUTATION_KEY, GR01_SAVE_MUTATION_KEY, useConfirmInbounds } from './queries/useModifyInbounds'
import { fromSearchQuery, restoredListQuery, toSearchQuery } from './queries/searchQuery'

// 일반입고 (gr/gr07 — 1.0 GR01 재구현) — .handoff/gr-gr01__20260826-1424 rebuild (pbo-core 데모 1:1, 2.0 API 배선)

const apiErrMessage = (e: unknown) => {
  const resp = (e as { response?: { status?: number; data?: { meta?: { message?: string } } } })?.response
  // 서버가 보낸 문구가 최우선이다.
  if (resp?.data?.meta?.message) return resp.data.meta.message
  if (resp?.status) return `처리에 실패했습니다. (HTTP ${resp.status})`
  // 응답이 아니라 화면 쪽 가드가 던진 Error 도 여기로 온다(화주 불일치 등). 그때 response 가 없어서
  // 문구가 통째로 "처리에 실패했습니다." 로 뭉개지고, 사용자는 왜 막혔는지 알 수 없었다.
  // GeneralInboundTable.tsx 에 같은 함수가 하나 더 있다 — 한쪽만 고치면 반쪽이 된다.
  if (e instanceof Error && e.message.trim()) return e.message
  return '처리에 실패했습니다.'
}

/**
 * 행 키 — 인덱스로 만들면 안 된다.
 *
 * `r-${index}` 를 쓰면 재조회로 같은 자리에 다른 행이 왔을 때 키가 그대로라 React 가 셀을
 * 재사용한다. pbo-core 의 `amountInput` 은 값을 내부 state 로 들고 prop 이 바뀌어도 따라오지
 * 않아서, 수량·입고단가 칸이 **이전 조회의 숫자를 그대로 보여준다**. 최소 Table 로 같은 키에
 * 111 → 999 를 넣어 확인했고 화면은 111 에 머문다. 사용자는 그 숫자를 믿고 저장·확정한다.
 *
 * 서버 id 를 쓴다(`IIF_MOMSAN_TYPE1.id`). 같은 행은 재조회해도 같은 키, 다른 행은 다른 키다.
 * id 가 비었거나 같은 id 가 두 번 오는 비정상 응답에서도 키가 겹치지 않게 보조키와 접미사를
 * 두되, 인덱스로는 돌아가지 않는다 — 그게 이 결함의 원인이었다.
 */
export const withRowKeys = (rows: GeneralInboundRow[]): InboundRow[] => {
  const used = new Set<string>()
  return rows.map((row, index) => {
    const base = row._id ? `r-${row._id}` : `r-n-${row.ORD_OPT_NO || row.BARCODE || index}`
    let key = base
    for (let dup = 1; used.has(key); dup += 1) key = `${base}-${dup}`
    used.add(key)
    return { ...row, _rowKey: key }
  })
}

function GeneralInbound() {
  // 타이틀 스티키 판정(컨덕터 동반 훅 사본) — 클래스 `dev-page-header-wrap pin` + 스크롤 중 `sticky`. 페인트는 DS CSS.
  const titleSticky = usePageHeaderSticky()
  // 버튼 권한·화면관리 그리드 설정(LOGISPRD-17250) — 손으로 짠 화면이라 생성본 슬롯 대신 여기서 불러 조회영역·표·[확정]에 나눠 준다.
  const permission = usePermissionSlot('gr07', { [GR07_TABLE_ID]: GR07_GRID_CD })
  // 조회 조건을 메뉴 세션(sessionStorage)·URL 쿼리에 남겨 다른 메뉴를 다녀와도 유지한다(MOMS 공통 UX — ST09·MA02 준용).
  const { reload } = useMenuOnSession()
  const { parsedQs, replaceQuery } = useParsedQuery<Record<string, unknown>>()
  // 마운트 시 한 번만 복원한다 — 이후 조회로 URL 이 바뀌어도 입력을 덮어쓰지 않는다.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const restoredParams = useMemo(() => fromSearchQuery(parsedQs), [])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const restoredList = useMemo(() => restoredListQuery(parsedQs), [])

  const [searchParams, setSearchParams] = useState<GeneralInboundParams>({})
  const searchParamsRef = useRef(searchParams)
  searchParamsRef.current = searchParams
  // onSearch 가 훅 선언보다 위에 있어 refetch·받은 행을 ref 로 잇는다.
  const refetchRef = useRef<((opts?: { cancelRefetch?: boolean }) => Promise<unknown>) | null>(null)
  const fetchedRef = useRef<GeneralInboundRow[]>([])
  // 편집 상태 — 받은 행을 로컬로 들고 셀 편집분과 엑셀 업로드 신규 행을 반영한다.
  const [rows, setRows] = useState<InboundRow[]>([])
  const [selected, setSelected] = useState<string[]>([])
  /** 마지막으로 표에 옮긴 받은 행 — 다음 구간이 뒤에 붙은 것인지(appendedRows) 가르는 기준 */
  const prevFetchedRef = useRef<GeneralInboundRow[]>([])
  /** 확정 뒤 비운 표 — 다음 [조회] 전까지 받은 행(확정 전 목록)이 다시 그려지지 않게 막는다 */
  const resetRef = useRef(false)

  /** 받은 행으로 표를 갈아 끼운다 — 편집분과 업로드 신규 행은 여기서 버려진다(그게 [조회] 의 뜻이다) */
  const replaceRows = (fetched: GeneralInboundRow[]) => {
    setRows(withRowKeys(fetched))
    setSelected([])
    prevFetchedRef.current = fetched
  }

  // 목록 검색어는 조회 조건이 아니라 로드된 행을 걸러 보는 값이지만 함께 URL 에 싣는다 —
  // 탭을 다녀왔을 때 조건만 살아나고 목록 검색어가 풀리면 사용자에게는 다른 화면으로 보인다.
  const [listQuery, setListQuery] = useState(restoredList)
  // 조회 버튼은 동일 조건이어도 항상 재조회한다(1.0 동작). 그 재조회를 queryKey 로 만들지 않는다 —
  // 마운트마다 0 으로 돌아가는 카운터를 키에 섞으면 탭을 다녀올 때 캐시가 통째로 어긋난다.
  // 조건은 그대로 두고 refetch 로 낸다. 조건이 방금 바뀐 경우 refetch 가 이전 키를 잡지 않도록
  // flushSync 로 먼저 커밋한다(stockLedger 준용).
  const onSearch = useCallback(
    (params: GeneralInboundParams, keepListQuery = '', forceRefetch = true) => {
      const searchQuery = toSearchQuery(params, keepListQuery)
      MenuStorage.menuOnSession.setSearch(searchQuery)
      reload()
      replaceQuery(searchQuery)
      // 확정 뒤 비운 표는 다음 [조회]부터 다시 받은 행을 그린다.
      resetRef.current = false
      const same = hashKey(generalInboundQueryKeys.list(params)) === hashKey(generalInboundQueryKeys.list(searchParamsRef.current))
      // [조회]는 편집분·업로드 행을 버린다(1.0). 같은 조건은 받은 행 참조가 그대로라 아래 effect 가 안 돌아 — 누르는 자리에서 받은 행으로 되돌린다.
      //  조건이 바뀌면 새 키의 행은 앞 행과 다른 객체라 effect 의 appendedRows 가 통째 교체로 가른다.
      if (forceRefetch && same) replaceRows(fetchedRef.current)
      flushSync(() => setSearchParams(params))
      // 복원 진입은 재조회하지 않는다 — 캐시가 살아 있으면 그대로 그려야 한다(결정 12).
      // [조회] 를 눌렀을 때만 조건이 같아도 서버에 다시 묻는다(키가 같으면 캐시라 자동 조회가 안 돈다).
      // cancelRefetch: false — 이미 나가 있는 조회가 있으면 취소하고 새로 띄우지 않고 거기에 합류한다.
      // 취소는 조용하고 queryFn 이 signal 을 안 써서 취소된 요청도 서버까지 그대로 간다 — [조회] 를 연타하면 조회가 두 번 나간다.
      // 실패 알럿은 공용 훅이 전역으로 띄운다.
      if (forceRefetch) refetchRef.current?.({ cancelRefetch: false }).catch(() => undefined)
    },
    // replaceRows 는 setState·ref 만 쓴다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reload, replaceQuery],
  )
  // 목록 검색어 변경 — 조회는 다시 하지 않고 URL·세션의 검색어만 갱신한다.
  const onListQueryChange = useCallback(
    (value: string) => {
      setListQuery(value)
      // 선택을 비운다. 목록 검색은 행을 가리기만 하고 선택은 그대로 남아서, 화면엔 3건인데
      // 저장·확정·인쇄는 가려진 행까지 대상으로 삼는다. 확정은 되돌릴 수 없어 특히 위험하다.
      // 화면에 보이는 것과 처리 대상이 어긋나지 않게 검색어가 바뀌면 선택을 푼다.
      setSelected([])
      const searchQuery = toSearchQuery(searchParams, value)
      MenuStorage.menuOnSession.setSearch(searchQuery)
      reload()
      replaceQuery(searchQuery)
    },
    [searchParams, reload, replaceQuery],
  )

  // 초기화 — 저장된 조건도 함께 비워 다음 진입이 기본값으로 열리게 한다(ST09 준용).
  // 목록·선택은 조회 결과가 사라지며 함께 비고, 목록 검색어는 listQuery 를 비우면 표의 제어형 입력이 따라온다.
  // 1.0 clearData 는 CommonUtil.onResetPage → GridActions.initData 로 그리드까지 비웠다.
  const [listResetToken, setListResetToken] = useState(0)
  const onReset = useCallback(() => {
    MenuStorage.menuOnSession.setSearch({})
    reload()
    replaceQuery({})
    setSearchParams({})
    // 표·선택을 직접 비운다 — 확정 뒤(resetRef)에는 조건이 비어 받은 행이 바뀌어도 effect 가 표를 건드리지 않아, 확정 뒤 올린 업로드 행이 남았다.
    resetRef.current = false
    prevFetchedRef.current = []
    setRows([])
    setSelected([])
    setListQuery('')
    // listQuery 가 이미 '' 이면 위 한 줄로는 표의 확정 전 입력(draft)이 안 비워진다 — 신호를 따로 보낸다.
    setListResetToken((t) => t + 1)
  }, [reload, replaceQuery])

  // 화주 옵션 — 화주코드가 조회·저장·확정 파라미터라 {value: 화주코드, label: 화주명} 으로 싣는다.
  const { data: shipperOptions = [] } = useQuery({
    queryKey: ['GR01_SHIPPER_OPTIONS'],
    meta: { errorToast: true }, // 실패는 옛 알럿 대신 새 디자인 알럿(queryClient.ts → notice.failure)
    queryFn: async () => {
      // BE 가 유형 목록을 IN 조건 한 번으로 처리한다 — 유형별로 나눠 부르지 않는다(LOGISPRD-16657).
      const r = await request.post<ApiResponseV2<{ items: { shipperCode: string; shipperName: string }[] }>, { logisticsServiceTypes: string[] }>(
        '/api/v1/shipper-filter-options',
        { logisticsServiceTypes: [...SHIPPER_TYPES] },
      )
      const unique = new Map((r.data?.items ?? []).map((s) => [s.shipperCode, s.shipperName]))
      return [...unique].map(([value, label]) => ({ value, label }))
    },
  })

  // 거래유형(LOT_ATTR5)·센터명(WH_NM)·다운로드 사유코드(DOWNLOAD_CD) — 화면 상수로 박아 두던 값
  // (dev 실측)은 운영 값이라는 근거가 없었다. 세 코드를 한 번에 묶어 API 응답으로 교체한다(LOGISPRD-12385).
  const { data: combos } = useGetCombos(['WH_NM', 'LOT_ATTR5', 'DOWNLOAD_CD'], { errorToast: true })
  // WH_NM 의 'ALL'(전체) 행은 편집 셀에 실려도 저장 가능한 실제 센터가 아니다(리뷰 반영).
  const whNmOptions = useMemo(() => toSelectOptions(excludeAllOption(combos?.WH_NM)), [combos])
  const transactionTypeOptions = useMemo(() => toSelectOptions(combos?.LOT_ATTR5), [combos])
  const downloadReasonOptions = useMemo(() => toSelectOptions(combos?.DOWNLOAD_CD), [combos])

  // 화주명은 필수 필터다 — 조건 없이 전체 조회하지 않는다.
  const hasSearchCondition = Boolean(searchParams.shipperCode)
  // 전량 조회(LOGISPRD-15268) — BE 상한 1,000 구간을 이어 받는다. 실패·10만 건 상한 안내는 공용 훅이 띄운다.
  const { rows: fetchedRows, isFirstLoading, isFetchingRest, refetch, guardAction } = useGetInboundList(searchParams, hasSearchCondition)
  refetchRef.current = refetch
  fetchedRef.current = fetchedRows

  useEffect(() => {
    if (resetRef.current) return
    // 남은 구간이 뒤에 붙은 것이면 붙은 행만 덧붙인다 — 받는 동안 한 편집·선택을 지우지 않는다.
    //  행 키는 서버 id 라 따로 만든 꼬리 키가 앞 행과 겹치지 않는다(같은 id 는 공용 훅이 거른다).
    const tail = appendedRows(prevFetchedRef.current, fetchedRows)
    if (tail) {
      setRows((prev) => [...prev, ...withRowKeys(tail)])
      prevFetchedRef.current = fetchedRows
    } else replaceRows(fetchedRows)
    // replaceRows 는 setState·ref 만 쓴다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedRows])

  const selectedRows = useMemo(() => rows.filter((r) => selected.includes(r._rowKey)), [rows, selected])

  const confirmInbounds = useConfirmInbounds()
  // 저장이 끝나기 전에 확정이 나가면 반영 안 된 값으로 컷오프가 잡히고 실행계까지 나간다.
  // 저장은 표 컴포넌트가 들고 있어 mutation key 로 진행 상태만 본다.
  const isSaving = useIsMutating({ mutationKey: GR01_SAVE_MUTATION_KEY }) > 0
  // 삭제도 같다 — 지우는 중인 행이 확정에 실리면 실행계로 나간 행을 지우거나 없는 id 로 확정한다.
  const isDeleting = useIsMutating({ mutationKey: GR01_DELETE_MUTATION_KEY }) > 0
  // isPending 은 mutate 가 시작돼야 true 가 된다. 모달이 떠 있는 동안과 확인 직후의 짧은 구간은
  // 상태 기반 잠금이 닿지 않는다. 확정은 실행계로 입고지시가 나가 되돌릴 수 없으므로
  // 상태보다 먼저 걸리는 ref 잠금을 둔다.
  const confirmLock = useRef(false)

  // 확정 — 1.0 FilterBar.onSend 의 GR01 분기(POST /api/v1/confirm-inbound)와 같은 계약.
  // 미저장 신규 행(엑셀 업로드분)은 id 가 없어 확정 대상이 아니다.
  const confirmable = useMemo(() => selectedRows.filter((r) => !r._isNew), [selectedRows])
  // 선택이 없으면 화주 전건 확정이다 — 1.0 과 BE(NormalInboundPlanService) 양쪽의 동작이다.
  const confirmsAll = confirmable.length === 0
  const onConfirm = async () => {
    if (confirmLock.current || confirmInbounds.isPending || isSaving || isDeleting) return
    // 받는 중 확정은 막는다 — 선택이 없으면 화주 전건 확정이라 받은 행과 무관하게 나가지만, 받는 중인 표를 보고 내린 판단이다.
    if (!guardAction()) return
    // 선택은 했는데 전부 미저장 신규 행이면, 그대로 보내면 전건 확정이 돼버린다. 막는다.
    if (selectedRows.length > 0 && confirmable.length === 0) {
      notice.invalid('선택한 행이 아직 저장되지 않았습니다. 저장 후 확정하세요.')
      return
    }
    // 저장된 행과 미저장 신규 행을 섞어 고른 경우 — 신규 행은 조용히 빠지므로 몇 건이 빠졌는지 알린다.
    const skipped = selectedRows.length - confirmable.length
    confirmLock.current = true
    try {
      // 요청을 먼저 띄운다 — 컨펌이 닫힌 뒤 isPending 이 켜지기 전 한 틱 동안 잠금이 비지 않게.
      await confirmInbounds.mutateAsync({ shipperCode: searchParams.shipperCode ?? '', rows: confirmable })
      if (skipped > 0) notice.failure(`미저장 신규 행 ${skipped}건은 확정에서 제외했습니다. 저장 후 다시 확정하세요.`)
      notice.success(confirmsAll ? '전건 확정 완료' : `${confirmable.length}건 확정 완료`)
      // 확정 후에는 목록을 비운다 (1.0 GridActions.initData 파리티).
      // 재조회하면 안 되는 이유가 있다 — BE get-normal-inbounds 는 확정된 행을 목록에서 빼지 않고
      // 화면에도 확정 여부 표시가 없다. 그대로 다시 그리면 방금 확정한 행이 미확정 행과 구분 없이
      // 남아 사용자가 같은 대상을 또 고른다. 조회 조건은 유지하므로 [조회]로 현재 상태를 다시 볼 수 있다.
      resetRef.current = true
      setSelected([])
      setRows([])
    } catch (e) {
      notice.failure(apiErrMessage(e))
    } finally {
      confirmLock.current = false
    }
  }

  // 확정 컨펌 — 컨덕터 `core.ConfirmModal`(inboundConfirmModal) = 2.0 공용 확인창(PboConfirm). 1.0 window.confirm('확정처리 하시겠습니까?') 파리티.
  // PboConfirm 은 [확인] 즉시 모달을 닫고 요청은 뒤에서 돌린다(이전 동작 동일).
  // 전건 확정 문구는 두 줄(사용자 확정 2026-09-10) — PboConfirm 이 문자열의 \n 을 pre-line 으로 살린다.
  const openConfirm = () => {
    if (!guardAction()) return
    PboConfirm.show(
      confirmsAll
        ? `선택한 행이 없습니다.\n화주 ${searchParams.shipperCode}의 미확정 일반 입고 건을 모두 확정하시겠어요?`
        : `선택한 ${confirmable.length}건을 확정 처리하시겠습니까?`,
      // onConfirm 은 안에서 오류를 처리하고 거절하지 않는다.
      { onConfirm },
    )
  }

  return (
    <PboScope>
      {/* 컨덕터 `cmp.TitleRow` 산출물(#inboundTitle) — 우측 [확정] primary·large. 스티키는 codegen 이 내보내는 형태와 동일
          (ref + `dev-page-header-wrap pin` + 어댑터가 인라인으로 되찾는 row·space-between·center·height auto·minHeight 40). DS 반영 시 DS 로 교체. */}
      <core.Flex
        ref={titleSticky.ref}
        className={`dev-page-header-wrap pin${titleSticky.isSticky ? ' sticky' : ''}`}
        gap={8}
        justify="space-between"
        align="center"
        style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', height: 'auto', minHeight: 40 }}
      >
        <core.Typography.Title level={4} style={{ margin: 0, whiteSpace: 'pre-line' }}>
          일반입고
        </core.Typography.Title>
        {/* 1.0 FilterBar.onSend 의 GR01 분기와 같은 API(confirm-inbound) — 1.0 SEND */}
        {permission.canShow('SEND') && (
          <core.Button type="primary" size="large" disabled={!hasSearchCondition || confirmInbounds.isPending || isSaving || isDeleting} onClick={openConfirm}>
            확정
          </core.Button>
        )}
      </core.Flex>
      <GeneralInboundFilter
        shipperOptions={shipperOptions}
        initialParams={restoredParams}
        // 조회할 때 화면의 현재 목록 검색어를 함께 실어야 URL·세션에서만 지워지는 일이 없다.
        initialListQuery={listQuery}
        onSearch={onSearch}
        onReset={onReset}
        canShow={permission.canShow}
      />
      <GeneralInboundTable
        rows={rows}
        setRows={setRows}
        selected={selected}
        setSelected={setSelected}
        searchParams={searchParams}
        shipperOptions={shipperOptions}
        transactionTypeOptions={transactionTypeOptions}
        whNmOptions={whNmOptions}
        downloadReasonOptions={downloadReasonOptions}
        onSaved={() => {
          // 저장·삭제 뒤 재조회는 받은 행으로 통째로 바꾼다 — 업로드 행이 남으면 같은 입고가 다시 생긴다.
          //  effect 에 맡기면 안 된다: 응답이 이전과 같으면 받은 행 참조가 그대로라 안 돌고, 업로드 뒤라 기준(prevFetchedRef)이 업로드 전 행이어서
          //  새 행이 뒤에 붙으면 '뒤에 덧붙음' 으로 읽어 업로드 행을 남긴다. 그래서 다시 받기에 성공하면 여기서 직접 바꾼다.
          //  응답이 바뀌었는데 아직 그려지기 전이면 옛 행으로 바뀌었다가, 새 행이 오면 effect 가 뒤에 붙은 것만 더한다.
          //  그 사이 조건이 바뀌었으면 넘긴다 — 새 조건의 행은 effect 가 그린다(지금 받은 행으로 바꾸면 표가 한 번 빈다).
          const searchedAtSave = searchParamsRef.current
          refetch()
            .then((res) => {
              if (!res.isError && searchParamsRef.current === searchedAtSave) replaceRows(fetchedRef.current)
            })
            .catch(() => undefined)
        }}
        guardAction={guardAction}
        loading={isFirstLoading || isFetchingRest}
        listQuery={listQuery}
        onListQueryChange={onListQueryChange}
        resetToken={listResetToken}
        permission={permission}
      />
    </PboScope>
  )
}

// 컨덕터 재핸드오프 게이트(문서 32)는 디자인 QA 검증 완료로 제거 — v2 셸 삭제, 이 화면이 곧 진입점(2026-09-11, 성준님 합의).
export default GeneralInbound

